import { liveOnly, personalDateOf, ReminderDecision } from '@wiztime/core';
import { queries } from '@wiztime/storage';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { dayPath } from '../../app/routes';
import { useNow } from '../../hooks/useNow';
import { useServices } from '../../hooks/useServices';
import { readStorage, writeStorage } from '../../lib/safeStorage';
import { StorageKey } from '../../lib/storageKeys';
import { notifyPermission, NotifyPermission, showReminderNotice } from '../reminder/notifier';
import { useReminderPrefs } from '../reminder/reminderPrefs';
import { logReminderDecision } from '../reminder/useCheckinReminders';
import { countdownLabel } from './format';
import { calendarDateOf, EMPTY_NOTIFIED_STATE, readNotifiedState, type NotifiedState } from './model';
import { loadCalendar } from './calendarModule';
import { checkImportantDateNotifications, withNotifyLock } from './notifications';

export function readStoredNotifiedState(): NotifiedState {
  const raw = readStorage(StorageKey.ImportantDateNotified);
  if (raw === null) return EMPTY_NOTIFIED_STATE;
  try {
    return readNotifiedState(JSON.parse(raw));
  } catch {
    return EMPTY_NOTIFIED_STATE;
  }
}

export function writeStoredNotifiedState(state: NotifiedState): void {
  writeStorage(StorageKey.ImportantDateNotified, JSON.stringify(state));
}

/**
 * System notifications of important dates (docs/requirements/wiztime-requirements.md §3.14): on
 * the first open of the app on a lead day, once per item per day. Uses the device switch and the
 * notification permission of the check-in reminders. Checks at start-up, when the calendar day
 * changes and when the page becomes visible again. Mounted once, in the app shell.
 */
export function useImportantDateNotifications(): void {
  const { store, now } = useServices();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const enabled = useReminderPrefs((prefs) => prefs.enabled);
  const today = calendarDateOf(useNow(60_000));
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    tRef.current = t;
    navigateRef.current = navigate;
  });

  useEffect(() => {
    // Without permission (never granted, or revoked) nothing is checked, so nothing is recorded as announced.
    if (!enabled || notifyPermission() !== NotifyPermission.Granted) return;
    let stopped = false;
    const check = async (): Promise<void> => {
      const day = calendarDateOf(now());
      try {
        const announced = await checkImportantDateNotifications(
          {
            loadDates: async () => liveOnly(await queries.importantDates(store.repo)),
            loadCalendar,
            readState: readStoredNotifiedState,
            writeState: writeStoredNotifiedState,
            withLock: withNotifyLock,
            show: (entry) =>
              showReminderNotice(
                {
                  title: entry.fields.title,
                  body: [countdownLabel(tRef.current, entry.daysUntil), entry.date].join(' · '),
                  tagSuffix: `importantDate-${entry.id}`,
                  keys: [],
                  buttons: [],
                },
                () => {
                  void queries.settings(store.repo).then((settings) => {
                    navigateRef.current(dayPath(personalDateOf(now(), settings.dayStartMin)));
                  });
                },
              ),
          },
          day,
        );
        if (!stopped)
          logReminderDecision(
            store,
            ReminderDecision.Shown,
            announced.map((id) => `importantDate|${id}|${day}`),
          );
      } catch (error) {
        console.warn('[important dates] notification check failed', error);
      }
    };
    void check();
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, store, now, today]);
}
